<template>
    <div v-if="additionals">
        <div v-if="model && additionals.salaVacinacaoLogada && !!additionals.salaVacinacaoLogada.id">
            <rkm-form ref="RkmForm">
                <rkm-area
                    title="Dados do paciente"
                    type="fieldset"
                >
                    <rkm-row>
                        <rkm-col>
                            <h4>
                                {{ model.cidadaoNome }}

                                <template v-if="model.cidadaoNomeSocial">
                                    <strong v-if="model.cidadaoPreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                        ({{ model.cidadaoNomeSocial }})
                                    </strong>

                                    <template v-else>
                                        ({{ model.cidadaoNomeSocial }})
                                    </template>
                                </template>

                                <span
                                    v-if="model.responsavelFamiliar"
                                    class="ml-1"
                                    title="Responsável familiar"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="crown" />
                                </span>

                                <span
                                    v-if="isTea"
                                    class="ml-1"
                                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="infinity" />
                                </span>

                                <span
                                    v-if="isPcd"
                                    class="ml-1"
                                    title="Pessoa com Deficiência - PCD"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="wheelchair" />
                                </span>
                            </h4>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="model.acompanhamentosSaude && model.acompanhamentosSaude.length > 0">
                        <rkm-col>
                            <div class="health-flags">
                                <span class="health-title">
                                    Acompanhamentos:
                                </span>

                                <span
                                    v-for="(acompanhamento, index) in model.acompanhamentosSaude"
                                    :key="index"
                                    :class="`health-badge acompanhamento-${acompanhamento.value}`"
                                >
                                    {{ acompanhamento.text }}
                                </span>
                            </div>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <p>
                                <strong>Prontuário:</strong>
                                <template v-if="model.pacienteProntuario">
                                    {{ model.pacienteProntuario }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>
                            <p>
                                <strong>RG:</strong>
                                <template v-if="model.cidadaoRg">
                                    {{ model.cidadaoRg }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Data de nascimento:</strong>
                                {{ formatDate(model.cidadaoDataNascimento) }}
                            </p>
                            <p>
                                <strong>Idade:</strong>
                                {{ model.cidadaoIdade }}
                            </p>
                            <p>
                                <strong>Sexo:</strong>
                                {{ model.sexoDescricao }}
                            </p>
                            <p>
                                <strong>Nome da mãe:</strong>
                                <template v-if="model.cidadaoNomeMae">
                                    {{ model.cidadaoNomeMae }}
                                </template>
                                <template v-else>
                                    <i>Desconhecido</i>
                                </template>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Classificação:</strong>
                                {{ classificacaoDescricao }}
                            </p>
                            <p>
                                <strong>Unidade de referência:</strong>
                                <template v-if="model.unidadeNomeFantasia">
                                    {{ model.unidadeNomeFantasia }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>
                            <p>
                                <strong>Área e Microárea:</strong>
                                {{ model.area || 'Não Informada' }} / {{ model.microarea || 'Não Informada' }}
                            </p>
                            <p>
                                <strong>INE:</strong>
                                {{ model.ine || 'Não Informado' }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <p>
                                <strong>Endereço:</strong>
                                <template v-if="enderecoCompleto">
                                    {{ enderecoCompleto }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row class="mt-2">
                        <rkm-col>
                            <rkm-form-input
                                v-model="model.cidadaoCns"
                                label="CNS"
                                :danger="!validaCns"
                                v-mask="mask.cns"
                                :validations="pacienteValidations.cns"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input
                                v-model="model.cidadaoCpf"
                                label="CPF"
                                :danger="!validaCpf"
                                v-mask="mask.cpf"
                                :validations="pacienteValidations.cpf"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input
                                v-model="model.cidadaoTelefone"
                                label="Telefone"
                                :disabled="!verificarPermissao('update-cadastro-paciente')"
                                :danger="!validaTelefone"
                                v-mask="model.cidadaoTelefone && model.cidadaoTelefone.length < 15 ? mask.phone : mask.cellPhone"
                                :validations="pacienteValidations.telefone"
                            />
                        </rkm-col>
                    </rkm-row>
                </rkm-area>

                <rkm-tabs>
                    <rkm-tab title="Aplicação / Atualização">
                        <rkm-area
                            no-margin-top
                            title="Lista de aplicação / atualização"
                        >
                            <template v-slot:trailing>
                                <rkm-button
                                    v-if="hasPermissaoAplicacao"
                                    @click="$refs.ModalAplicacao.open()"
                                    color="primary"
                                    :disabled=!!model.obito
                                    small
                                >
                                    Nova aplicação
                                </rkm-button>

                                <rkm-button
                                    @click="$refs.ModalAtualizacao.open()"
                                    color="primary"
                                    :disabled=!!model.obito
                                    small
                                >
                                    Nova atualização
                                </rkm-button>
                            </template>

                            <rkm-row>
                                <rkm-col cols="12">
                                    <rkm-table
                                        :fields="fields.registro"
                                        :items="model.registros"
                                        :tbody-tr-class="rowClass"
                                    >
                                        <template v-slot:cell(dataRegistro)="data">
                                            {{ formatarData(data.item.dataRegistro) }}
                                        </template>

                                        <template v-slot:cell(dataCadastro)="data">
                                            {{ formatarData(data.item.dataCadastro) }}
                                        </template>

                                        <template v-slot:cell(dataAprazamento)="data">
                                            {{ getDataAprazamento(data.item) }}
                                        </template>

                                        <template v-slot:cell(registroTipo)="{ item }">
                                            <div class="text-nowrap">
                                                <template v-if="item.registroTipo === 'aplicacao'">Aplicação</template>
                                                <template v-else>Atualização</template>

                                                <rkm-icon
                                                    v-if="item.statusRia === 'pendente'"
                                                    icon="clock"
                                                    title="Pendente"
                                                    class="ml-1"
                                                />

                                                <template v-else-if="item.statusRia === 'enviado'">
                                                    <rkm-icon
                                                        v-if="!!item.riaAtualizado"
                                                        icon="sync-alt"
                                                        title="Este registro já foi corrigido e reenviado, conforme as regras do integrador."
                                                        class="ml-1"
                                                    />

                                                    <rkm-icon
                                                        v-else
                                                        icon="share"
                                                        title="Enviado"
                                                        class="ml-1"
                                                    />
                                                </template>

                                                <rkm-icon
                                                    v-else-if="item.statusRia === 'rejeitado'"
                                                    icon="ban"
                                                    title="Rejeitado"
                                                    class="ml-1"
                                                />
                                            </div>
                                        </template>

                                        <template v-slot:cell(actions)="{ index, item }">
                                            <rkm-button
                                                @click="$refs.ModalDetalhe.open(item)"
                                                color="primary"
                                                small
                                                title="Exibir detalhes"
                                            >
                                                <rkm-icon icon="bars" />
                                            </rkm-button>

                                            <template v-if="item.registroTipo === 'aplicacao'">
                                                <rkm-button
                                                    @click="$refs.ModalAplicacao.open(item, true)"
                                                    small
                                                    title="Visualizar"
                                                >
                                                    <rkm-icon icon="eye" />
                                                </rkm-button>

                                                <rkm-button
                                                    :disabled="!!item.exportado"
                                                    @click="$refs.ModalAplicacao.open(item, false, false, index)"
                                                    color="primary"
                                                    small
                                                    title="Editar"
                                                >
                                                    <rkm-icon icon="pen" />
                                                </rkm-button>

                                                <rkm-button
                                                    :disabled="!!item.exportado"
                                                    @click="destroy(item)"
                                                    authorize="destroy-imunizacao-registro-vacinacao"
                                                    color="danger"
                                                    small
                                                    title="Excluir"
                                                >
                                                    <rkm-icon icon="trash" />
                                                </rkm-button>
                                            </template>

                                            <template v-else>
                                                <rkm-button
                                                    @click="$refs.ModalAtualizacao.open(item, true)"
                                                    small
                                                    title="Visualizar"
                                                >
                                                    <rkm-icon icon="eye" />
                                                </rkm-button>

                                                <rkm-button
                                                    :disabled="!!item.exportado"
                                                    @click="$refs.ModalAtualizacao.open(item, false, index)"
                                                    color="primary"
                                                    small
                                                    title="Editar"
                                                >
                                                    <rkm-icon icon="pen" />
                                                </rkm-button>

                                                <rkm-button
                                                    :disabled="!!item.exportado"
                                                    @click="destroyAtualizacao(item)"
                                                    authorize="destroy-imunizacao-registro-vacinacao"
                                                    color="danger"
                                                    small
                                                    title="Excluir"
                                                >
                                                    <rkm-icon icon="trash" />
                                                </rkm-button>
                                            </template>
                                        </template>
                                    </rkm-table>
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>
                    </rkm-tab>

                    <rkm-tab title="Aprazamento">
                        <rkm-area
                            no-margin-top
                            title="Lista de aprazamento"
                        >
                            <template v-slot:trailing>
                                <rkm-button
                                    @click="$refs.ModalAprazamento.open()"
                                    :disabled=!!model.obito
                                    color="primary"
                                    small
                                    title="Novo aprazamento"
                                >
                                    <rkm-icon icon="plus" />
                                </rkm-button>
                            </template>

                            <rkm-row>
                                <rkm-col cols="12">
                                    <rkm-table
                                        :fields="fields.aprazamento"
                                        :items="model.aprazamentos"
                                    >
                                        <template v-slot:cell(actions)="{ index, item }">
                                            <rkm-button
                                                :disabled="handleDisabledAprazamento(item)"
                                                @click="$refs.ModalAplicacao.open(item, false, true)"
                                                color="info"
                                                small
                                                title="Aplicar"
                                            >
                                                <rkm-icon icon="syringe" />
                                            </rkm-button>

                                            <rkm-button
                                                @click="$refs.ModalAprazamento.open(item, true)"
                                                small
                                                title="Visualizar"
                                            >
                                                <rkm-icon icon="eye" />
                                            </rkm-button>

                                            <rkm-button
                                                @click="$refs.ModalAprazamento.open(item, false, index)"
                                                color="primary"
                                                small
                                                title="Editar"
                                            >
                                                <rkm-icon icon="pen" />
                                            </rkm-button>

                                            <rkm-button
                                                :disabled="!!item.registroAplicacaoId"
                                                @click="destroyAprazamento(item.id)"
                                                authorize="destroy-imunizacao-registro-vacinacao"
                                                color="danger"
                                                small
                                                title="Excluir"
                                            >
                                                <rkm-icon icon="trash" />
                                            </rkm-button>
                                        </template>
                                    </rkm-table>
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>
                    </rkm-tab>
                </rkm-tabs>
            </rkm-form>

            <ModalAplicacao
                :additionals="additionals"
                :form="form"
                :id="id"
                :model="model"
                :registrosExcluidos="registrosExcluidos"
                ref="ModalAplicacao"
            />

            <ModalAtualizacao
                :additionals="additionals"
                :form="form"
                :id="id"
                :model="model"
                :registrosExcluidos="registrosExcluidos"
                ref="ModalAtualizacao"
            />

            <ModalAprazamento
                :additionals="additionals"
                :form="form"
                :id="id"
                :model="model"
                ref="ModalAprazamento"
            />

            <ModalDetalhe
                ref="ModalDetalhe"
            />
        </div>

        <rkm-alert v-else color="warning">
            A unidade logada não é uma sala de vacinação ou está desativada, favor cadastra-la para efetuar um registro de vacinação.
        </rkm-alert>
    </div>
</template>

<script>
// Components.
import ModalAplicacao from './components/aplicacao/Modal'
import ModalAprazamento from './components/aprazamento/Modal'
import ModalAtualizacao from './components/atualizacao/Modal'
import ModalDetalhe from './components/detalhe/Modal'

// Enums.
import parametrizacaoDescricaoVacina from '@/enums/imunizacao/parametrizacaoDescricaoVacina'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import { error, formats, permissao, repository, stringCase, mask, unmask } from '@/mixins'

// Others.
import moment from 'moment'

// Utils.
import cns from '@/utils/validations/cns'
import cpf from '@/utils/validations/cpf'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: {
        ModalAplicacao,
        ModalAprazamento,
        ModalAtualizacao,
        ModalDetalhe
    },
    computed: {
        classificacaoDescricao() {
            if (this.model.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (this.model.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.model.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.model.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.model.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.model.deficienciaId && this.model.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        enderecoCompleto() {
            return [
                this.model.logradouroNome,
                this.model.enderecoNumero,
                this.model.enderecoComplemento,
                this.model.enderecoReferencia,
                this.model.bairroNome,
                this.model.municipioNome,
                this.model.estadoNome,
            ].filter(Boolean).join(', ')
        }, 
        validaCns() {
            if (!this.additionals.parametrizacao.cnsCpfObrigatorio) {
                return true
            }

            return this.validations.cns(this.model.cidadaoCns)
        },
        validaCpf() {
            if (!this.additionals.parametrizacao.cnsCpfObrigatorio) {
                return true
            }

            return this.validations.cpf(this.model.cidadaoCpf)
        },
        validaTelefone() {
            return this.model.cidadaoTelefone && (this.model.cidadaoTelefone.length == 14 || this.model.cidadaoTelefone.length == 15)
        },
        fields() {
            const commonFields = []

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    commonFields.push({ key: 'vacinaAbreviacao', label: 'Abreviação da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    commonFields.push({ key: 'vacinaSigla', label: 'Sigla da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    commonFields.push({ key: 'vacinaSiglaNome', label: 'Sigla e nome da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    commonFields.push({ key: 'vacinaSiglaAbreviacao', label: 'Sigla e abreviação da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    commonFields.push({ key: 'vacinaAbreviacaoNome', label: 'Abreviação e nome da vacina' })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    commonFields.push({ key: 'vacinaSiglaAbreviacaoNome', label: 'Sigla, abreviação e nome da vacina' })
                    break;

                default:
                    commonFields.push({ key: 'vacinaNome', label: 'Nome da vacina' })
                    break;
            }

            return {
                registro: [
                    { key: 'registroTipo', label: 'Tipo de registro' },
                    ...commonFields,
                    { key: 'doseNome', label: 'Dose' },
                    { key: 'dataRegistro', label: 'Data da aplicação', sortable: true },
                    { key: 'dataCadastro', label: 'Data do registro', sortable: true },
                    { key: 'dataAprazamento', label: 'Data do aprazamento', sortable: true },
                    { key: 'unidadeNomeFantasia', label: 'Unidade' },
                    { key: 'salaVacinacaoNome', label: 'Sala de vacinação' },
                    { key: 'observacao', label: 'Observação' },
                    { class: 'col-min text-center', key: 'actions', label: 'Ações' },
                ],
                aprazamento: [
                    ...commonFields,
                    { key: 'doseNome', label: 'Dose' },
                    { key: 'dataAprazamento', label: 'Data do aprazamento' },
                    { key: 'observacao', label: 'Observação' },
                    { class: 'col-min text-center', key: 'actions', label: 'Ações' },
                ],
            }
        },
        pacienteValidations() {
            if (!this.additionals.parametrizacao.cnsCpfObrigatorio) {
                return {
                    cns: {},
                    cpf: {},
                    telefone: {
                        min_max_without_mask: [10, 11]
                    }
                }
            }

            return {
                cns: {
                    required: true,
                    min_max_without_mask: 15,
                    cns: true,
                },
                cpf: {
                    required: true,
                    min_max_without_mask: 11,
                    cpf: true
                },
                telefone: {
                    min_max_without_mask: [10, 11]
                }
            }
        },
        hasPermissaoAplicacao() {
            let hasPermissao = true

            if (this.additionals && this.additionals.parametrizacao) {
                if (this.model && this.model.classificacaoId === this.enums.classificacao.OUTRO_MUNICIPIO && !this.additionals.parametrizacao.cadastroOutroMunicipio) {
                    hasPermissao = false
                }
                else if (this.model && this.model.classificacaoId === this.enums.classificacao.PROVISORIO && !this.additionals.parametrizacao.cadastroProvisorio) {
                    hasPermissao = false
                }
                else if (this.model && this.model.bloqueado && !this.additionals.parametrizacao.cadastroBloqueado) {
                    hasPermissao = false
                }
                else if (this.model && this.model.cadastroDesatualizado && !this.additionals.parametrizacao.cadastroDesatualizado) {
                    hasPermissao = false
                }
            }

            return hasPermissao
        },
    },
    created() {
        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.imunizacao'),
            registroVacinacao: this.getConectaSusRepository('imunizacao.registroVacinacao'),
        }
    },
    data: () => ({
        additionals: null,
        enums: {
            parametrizacaoDescricaoVacina,
            classificacao,
            subclassificacao,
            preferenciaNome,
            deficienciaBase,
            deficiencia,
        },
        form: null,
        model: null,
        others: { moment },
        validations: { cns, cpf },
        registrosExcluidos: [],
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            const request = {
                pacienteId: this.id,
                filaId: this.filaId,
            }

            this.setLoading()

            this.repositories.registroVacinacao.create(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)


                    this.$emit('loaded', { additionals: this.additionals, model: this.model })

                    if (this.additionals.salaVacinacaoLogada && !!this.additionals.salaVacinacaoLogada.id) {
                        this.showMensagemAprazamento()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        showMensagemAprazamento() {
            if (this.additionals.parametrizacao && this.additionals.parametrizacao.aprazamentoAtivo) {
                let mensagensAprazamento = []

                this.model.aprazamentos.forEach(aprazamento => {
                    const dataAtual = this.others.moment(this.additionals.dataAtual)
                    const dataAprazamento = this.others.moment(aprazamento.dataAprazamento, 'DD/MM/YYYY')
                    const diferencaDataAtualDataAprazamento = dataAtual.diff(dataAprazamento, 'days')

                    if (dataAtual > dataAprazamento) {
                        if (diferencaDataAtualDataAprazamento <= this.additionals.parametrizacao.aprazamentoDiasDepois) {
                            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                                case this.enums.parametrizacaoDescricaoVacina.ABREVIACAO:
                                    mensagensAprazamento.push(`${aprazamento.vacinaAbreviacao} - ${aprazamento.dataAprazamento}`)
                                    break;

                                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                                    mensagensAprazamento.push(`${aprazamento.vacinaSigla} - ${aprazamento.dataAprazamento}`)
                                    break;

                                default:
                                    mensagensAprazamento.push(`${aprazamento.vacinaNome} - ${aprazamento.dataAprazamento}`)
                                    break;
                            }
                        }
                    }
                    else if (dataAtual <= dataAprazamento) {
                        if ((diferencaDataAtualDataAprazamento * -1) <= this.additionals.parametrizacao.aprazamentoDiasAntes) {
                            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                                case this.enums.parametrizacaoDescricaoVacina.ABREVIACAO:
                                    mensagensAprazamento.push(`${aprazamento.vacinaAbreviacao} - ${aprazamento.dataAprazamento}`)
                                    break;

                                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                                    mensagensAprazamento.push(`${aprazamento.vacinaSigla} - ${aprazamento.dataAprazamento}`)
                                    break;

                                default:
                                    mensagensAprazamento.push(`${aprazamento.vacinaNome} - ${aprazamento.dataAprazamento}`)
                                    break;
                            }
                        }
                    }
                })

                if (mensagensAprazamento.length > 0) {
                    let mensagem = ''

                    mensagensAprazamento.forEach(mensagemAprazamento => {
                        mensagem = mensagem + `<li>${mensagemAprazamento}</li>`
                    })

                    this.showAlert(
                        `
                            O paciente possui vacinas com aprazamentos pendentes ou próximos:
                            <ul>${mensagem}</ul>
                        `,
                        'Atenção'
                    )
                }
            }
        },
        destroy(item) {
            if (item.id) {
                this.registrosExcluidos.push(item)
            }

            this.showDeleteConfirmation('Deseja realmente excluir o registro?', async () => this.model.registros.splice(this.model.registros.findIndex(r => r.id == item.id && r.registroTipo == 'aplicacao'), 1))
        },
        destroyAtualizacao(item) {
            if (item.id) {
                this.registrosExcluidos.push(item)
            }

            this.showDeleteConfirmation('Deseja realmente excluir a atualização?', async () => this.model.registros.splice(this.model.registros.findIndex(r => r.id == item.id && r.registroTipo == 'atualizacao'), 1))
        },
        destroyAprazamento(id) {
            this.showDeleteConfirmation('Deseja realmente excluir o aprazamento?', async () => this.model.aprazamentos.splice(this.model.aprazamentos.findIndex(a =>a.id == id), 1))
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if(valid) {
                        try {
                            const request = {
                            pacienteId: this.id,
                           cidadaoCns: this.model.cidadaoCns
                                ? this.unMaskCns(this.model.cidadaoCns)
                                : null,
                            cidadaoCpf: this.model.cidadaoCpf
                                ? this.unMaskCpf(this.model.cidadaoCpf)
                                : null,
                            cidadaoTelefone: this.unMaskPhone(this.model.cidadaoTelefone),
                            aplicacoes: this.model.registros.filter(registro => registro.registroTipo === 'aplicacao')
                                .map(aplicacao => {
                                    return {
                                        id: aplicacao.id,
                                        salaVacinacaoId: aplicacao.salaVacinacaoId,
                                        aplicadorId: aplicacao.aplicadorId,
                                        ocupacaoId: aplicacao.ocupacaoId,
                                        equipeId: aplicacao.equipeId,
                                        turnoId: aplicacao.turnoId,
                                        localAtendimentoId: aplicacao.localAtendimentoId,
                                        dataRegistro: aplicacao.dataRegistro,
                                        vacinaId: aplicacao.vacinaId,
                                        lote: aplicacao.lote ? aplicacao.lote.lote : null,
                                        entradaId: aplicacao.lote ? aplicacao.lote.id : null,
                                        dataValidadeLote: aplicacao.lote ? aplicacao.lote.dataValidadeLote : null,
                                        produtorId: aplicacao.produtorId,
                                        grupoAtendimentoId: aplicacao.grupoAtendimentoId,
                                        doseId: aplicacao.doseId,
                                        estrategiaId: aplicacao.estrategiaId,
                                        viaAdministracaoId: aplicacao.viaAdministracaoId,
                                        localAplicacaoId: aplicacao.localAplicacaoId,
                                        cidId: aplicacao.cidId,
                                        comunicanteHanseniase: aplicacao.comunicanteHanseniase,
                                        gestante: aplicacao.gestante,
                                        puerpera: aplicacao.puerpera,
                                        imunizacaoAdvertida: aplicacao.imunizacaoAdvertida,
                                        viajante: aplicacao.viajante,
                                        ultimoFrasco: aplicacao.ultimoFrasco,
                                        observacao: aplicacao.observacao,
                                        aprazamentoId: aplicacao.aprazamentoId,
                                        alterou: aplicacao.alterou ?? false,
                                    }
                                }),
                            atualizacoes: this.model.registros.filter(registro => registro.registroTipo === 'atualizacao')
                                .map(atualizacao => {
                                    return {
                                        aplicadorId: atualizacao.aplicadorId,
                                        dataRegistro: atualizacao.dataRegistro,
                                        doseId: atualizacao.doseId,
                                        estrategiaId: atualizacao.estrategiaId,
                                        entradaId: atualizacao.lote ? atualizacao.lote.id : null,
                                        equipeId: atualizacao.equipeId,
                                        grupoAtendimentoId: atualizacao.grupoAtendimentoId,
                                        id: atualizacao.id,
                                        localAtendimentoId: atualizacao.localAtendimentoId,
                                        lote: atualizacao.lote,
                                        cidId: atualizacao.cidId,
                                        observacao: atualizacao.observacao,
                                        ocupacaoId: atualizacao.ocupacaoId,
                                        salaVacinacaoId: atualizacao.salaVacinacaoId,
                                        turnoId: atualizacao.turnoId,
                                        vacinaId: atualizacao.vacinaId,
                                        alterou: atualizacao.alterou ?? false,
                                    }
                                }),
                            aprazamentos: this.model.aprazamentos.map(aprazamento => {
                                return {
                                    id: aprazamento.id,
                                    dataAprazamento: aprazamento.dataAprazamento,
                                    vacinaId: aprazamento.vacinaId,
                                    doseId: aprazamento.doseId,
                                    observacao: aprazamento.observacao,
                                }
                            }),
                            filaId: this.filaId,
                        }

                        this.setLoading()

                            this.repositories.registroVacinacao.store(this.camelObjectKeysToSnakeRecursive(request))
                                .then(() => {
                                    this.showSaveNotification()
                                    this.$emit('save')
                                })
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading())
                        } catch (error) {
                            this.handleError(error)
                        }
                    }
                })
        },
        printRegistroVacinacao() {
            this.setLoading()

            this.repositories.impressao.registroVacinacao(this.id)
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        printCarteiraVacinacao() {
            this.setLoading()

            this.repositories.impressao.carteiraVacinacao(this.id)
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        printCertidaoRegularidade() {
            this.setLoading()

            this.repositories.impressao.certidaoRegularidade(this.id)
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleDisabledAprazamento(item) {

            if (this.others.moment(this.additionals.dataAtual) >= this.others.moment(item.dataAprazamento, 'DD/MM/YYYY')) {
                return false
            }

            if (this.additionals.parametrizacao && this.additionals.parametrizacao.aprazamentoAtivo) {

                const dataAtual = this.others.moment(this.additionals.dataAtual)
                const dataAprazamento = this.others.moment(item.dataAprazamento, 'DD/MM/YYYY')
                const diferencaDataAtualDataAprazamento = dataAtual.diff(dataAprazamento, 'days')

                if (dataAtual <= dataAprazamento) {
                    if ((diferencaDataAtualDataAprazamento * -1) <= this.additionals.parametrizacao.aprazamentoDiasAntes) {
                        return false
                    }
                }
            }

            return true
        },
        formatarData(data) {
            if (data instanceof Date) {
                return moment(data).format('DD/MM/YYYY');
            }
            if (typeof data === 'string') {
                const parsedDate = moment(data, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ','DD/MM/YYYY'], true);
                return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : 'Data inválida';
            }
        },
        rowClass(item, type) {
            if (type === 'row' && item && item.statusRia !== 'pendente' && item.riaUsuarioAtualizou) {
                return 'table-warning'
            }
        },
        getDataAprazamento(item) {
            const aprazamento = this.model.aprazamentos.find(a => a.vacinaId == item.vacinaId && a.doseId == item.proximaDoseId)

            if (aprazamento) {
                return aprazamento.dataAprazamento
            }
            else if (item.dataAprazamento) {
                return this.formatarData(item.dataAprazamento)
            }

            return ''
        }
    },
    mixins: [ error, formats, permissao, repository, stringCase, mask, unmask ],
    mounted() {
        this.load()
    },
    props: {
        filaId: { type: Number, default: null },
        id: { type: Number, default: null },
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>